/**
 * Which residential-care FEE ARRANGEMENTS a resident is on — the 1 November 2025 rules (hotelling
 * and non-clinical care contributions) or the 1 July 2014 rules (the means-tested care fee) kept
 * open by the "no worse off" principle.
 *
 * THE RULE (Department of Health, "Fee and accommodation arrangements for residential aged care",
 * updated 19 Dec 2025; "Means tested fees for residential aged care"; residential fee scenarios for
 * the 1 July 2014 arrangements, rates at 20 Mar 2026; the Department's residential fees webinar FAQ,
 * Q2.10; verified 17 Sep 2026):
 *
 *   A person who on 12 September 2024 was receiving a Home Care Package, approved as eligible for
 *   one, or waiting for one on the National Priority System, and who first enters residential care
 *   on or after 1 November 2025, is on the 1 JULY 2014 FEE ARRANGEMENTS: a basic daily fee and a
 *   MEANS-TESTED CARE FEE, and no hotelling or non-clinical care contribution. They may opt in to the
 *   1 November 2025 fee arrangements at any time, and cannot undo it.
 *
 *   Their ACCOMMODATION is on the 1 November 2025 arrangements regardless — deposit retention and
 *   DAP indexation apply — because accommodation grandfathering only covers people already living in
 *   a home on 31 October 2025. Nothing here touches accommodation.
 *
 * The profile records the answer per person (`careNeeds.<person>Acat.hcpBeforeSep2024`); this
 * module is the one place that turns it into a regime, so the fee engine, the projection, the
 * suggestion engine, the screens and the advice document cannot disagree about which rules a
 * client is on. "Unsure" is treated as the 1 November 2025 rules — the arrangements that charge
 * more — and the advice document says so.
 */
import type { AcatPerson, FactFind } from './factFindTypes.ts';

export type ResidentialFeeArrangements = '2025' | '2014';

/** Strategy options this module reads; kept structural so callers need not import the full type. */
export interface FeeArrangementsStrategyOptions {
    /**
     * An explicit choice for the run — `'2025'` models the resident OPTING IN to the 1 November
     * 2025 fee arrangements. `'2014'` is honoured only for a resident the profile says is covered
     * by the no-worse-off principle; nobody else can be on those arrangements.
     */
    residentialFeeArrangements?: ResidentialFeeArrangements | null;
    [key: string]: unknown;
}

/** Whether one person's profile answer puts them under the no-worse-off principle. */
export function isCoveredByNoWorseOffPrinciple(acat: AcatPerson | null | undefined): boolean {
    return acat?.hcpBeforeSep2024 === 'yes';
}

/**
 * The people whose fees the aged-care modeller is pricing. A single client is always the client;
 * a couple's `goals.aged_care_for` names who is moving (the client when unset — the modeller's
 * own convention, see householdStatus.getAgedCareModellerResidencyOverrides).
 */
export function residentsEnteringCare(factFind: FactFind | null | undefined): Array<'client' | 'partner'> {
    if (factFind?.adviceFor !== 'couple') return ['client'];
    const forWho = factFind?.goals?.aged_care_for;
    if (forWho === 'partner') return ['partner'];
    if (forWho === 'both') return ['client', 'partner'];
    return ['client'];
}

function acatOf(factFind: FactFind | null | undefined, person: 'client' | 'partner'): AcatPerson | undefined {
    return person === 'partner' ? factFind?.careNeeds?.partnerAcat : factFind?.careNeeds?.clientAcat;
}

/**
 * True when EVERY person entering care is covered by the no-worse-off principle. Where a couple
 * enters together and only one is covered, the household is priced on the 1 November 2025 rules —
 * the fee engine strikes one schedule for the household and the dearer rules are the safe
 * assumption; the advice document names the person whose answer decided it.
 */
export function householdCoveredByNoWorseOffPrinciple(factFind: FactFind | null | undefined): boolean {
    const residents = residentsEnteringCare(factFind);
    return residents.length > 0 && residents.every((person) => personCoveredByNoWorseOffPrinciple(factFind, person));
}

/** The first day of the 1 November 2025 arrangements. */
export const NEW_ARRANGEMENTS_FROM = '2025-11-01';

/**
 * Someone ALREADY in residential care who entered before 1 November 2025 is an existing resident on
 * the 1 July 2014 arrangements — the "no worse off" principle covers them as it does a Home Care
 * Package holder (owner's change-of-home work, 8 Oct 2026). Leaving care for more than 28 days ends
 * it; a plain move between homes does not.
 */
export function isExistingResidentBeforeNewArrangements(factFind: FactFind | null | undefined, person: 'client' | 'partner'): boolean {
    const p = person === 'partner' ? factFind?.partner : factFind?.client;
    const entry = p?.careStay?.entryDate;
    return p?.currentResidency === 'aged_care' && typeof entry === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(entry) && entry < NEW_ARRANGEMENTS_FROM;
}

/** One person's answer: a Home Care Package before 12 September 2024, or an existing resident from before 1 November 2025. */
export function personCoveredByNoWorseOffPrinciple(factFind: FactFind | null | undefined, person: 'client' | 'partner'): boolean {
    return isCoveredByNoWorseOffPrinciple(acatOf(factFind, person)) || isExistingResidentBeforeNewArrangements(factFind, person);
}

/**
 * The fee arrangements a run prices against. The profile decides; the strategy options can only
 * move a covered resident ONTO the 1 November 2025 rules (opting in), never anyone onto the 2014
 * rules.
 */
export function resolveResidentialFeeArrangements(
    factFind: FactFind | null | undefined,
    strategyOptions: FeeArrangementsStrategyOptions | null | undefined = {},
    /**
     * ONE person's arrangements, where the run prices two residents separately (7 Oct 2026). Absent,
     * the household rule above applies — every resident covered, or the 2025 rules for all.
     */
    person?: 'client' | 'partner',
): ResidentialFeeArrangements {
    const covered = person ? personCoveredByNoWorseOffPrinciple(factFind, person) : householdCoveredByNoWorseOffPrinciple(factFind);
    if (!covered) return '2025';
    return strategyOptions?.residentialFeeArrangements === '2025' ? '2025' : '2014';
}

/**
 * Care contributions the residents entering care have ALREADY paid before this run — income-tested
 * care fees under a Home Care Package and Support at Home contributions. They count towards the
 * lifetime caps on both sets of arrangements (the means-tested care fee's, and the non-clinical
 * care contribution's dollar cap — not its 1,460-day cap), so a run seeds its cap counters with
 * them. Recorded on the profile as a single figure per person; nil when not recorded.
 */
export function priorCareContributionsPaid(factFind: FactFind | null | undefined, person?: 'client' | 'partner'): number {
    // One person's own figure where the run keeps a cap counter per resident (7 Oct 2026).
    if (person) return Math.max(0, Number(acatOf(factFind, person)?.priorCareContributionsPaid) || 0);
    return residentsEnteringCare(factFind).reduce((sum, person) => {
        const paid = Number(acatOf(factFind, person)?.priorCareContributionsPaid) || 0;
        return sum + Math.max(0, paid);
    }, 0);
}

/** The arrangements in the words the Department uses, for screens and the advice document. */
export function describeResidentialFeeArrangements(arrangements: ResidentialFeeArrangements): string {
    return arrangements === '2014'
        ? '1 July 2014 fee arrangements (means-tested care fee; no hotelling or non-clinical care contribution)'
        : '1 November 2025 fee arrangements (hotelling and non-clinical care contributions)';
}
